import { z } from 'zod';
import { command, del, get, post } from '../http';
import { type ListQuery, paginated, resource } from '../schemas/common';

/**
 * 2026-09-11, LDS1 subdomain only: "pay USDT, get LDS1 sent automatically to
 * any wallet" — client's explicit requirement (see
 * App\Services\Purchase\Lds1PurchaseService's docblock on the API side for
 * the full flow). The sender wallet's private key never appears in any of
 * these shapes — same write-only discipline as autoWithdrawal above, just for
 * a completely separate, independent hot wallet.
 */
const lds1PurchaseRequestSchema = z.object({
  id: z.string(),
  recipient_wallet_address: z.string(),
  usdt_amount_expected: z.string(),
  usdt_amount_confirmed: z.string().nullable(),
  lds1_amount: z.string().nullable(),
  tx_hash: z.string().nullable(),
  send_tx_hash: z.string().nullable(),
  status: z.enum(['pending', 'confirmed', 'failed', 'rejected']),
  reject_reason: z.string().nullable(),
  created_at: z.string().nullable(),
});
export type Lds1PurchaseRequestItem = z.infer<typeof lds1PurchaseRequestSchema>;

const lds1PurchaseResultSchema = z.object({
  status: z.enum(['pending', 'confirmed', 'failed', 'rejected']),
  request: lds1PurchaseRequestSchema,
});

const lds1PurchaseInfoSchema = z.object({
  deposit_address: z.string(),
  chain: z.string(),
  lds_rate_usd: z.string(),
  max_usd: z.string(),
});
export type Lds1PurchaseInfo = z.infer<typeof lds1PurchaseInfoSchema>;

const lds1PurchaseAdminSettingsSchema = z.object({
  enabled: z.boolean(),
  has_key: z.boolean(),
  wallet_address: z.string().nullable(),
  key_set_at: z.string().nullable(),
  max_per_tx_usd: z.string(),
  max_per_day_usd: z.string(),
  gas_limit_units: z.number(),
  sender_wallet_bnb_balance: z.string().nullable(),
  sender_wallet_lds1_balance: z.string().nullable(),
  deposit_address: z.string(),
});
export type Lds1PurchaseAdminSettings = z.infer<typeof lds1PurchaseAdminSettingsSchema>;

const lds1PurchaseReportRowSchema = z.object({
  id: z.string(),
  user_code: z.string().nullable(),
  recipient_wallet_address: z.string(),
  usdt_amount_expected: z.string().optional(),
  usdt_amount_confirmed: z.string().nullable(),
  lds1_amount: z.string().optional(),
  tx_hash: z.string().nullable(),
  reject_reason: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
});

const lds1PurchaseReportSchema = z.object({
  sent_count: z.number(),
  sent_total_usd: z.string(),
  sent_today_usd: z.string(),
  pending_count: z.number(),
  failed_count: z.number(),
  pending: z.array(lds1PurchaseReportRowSchema),
  failed: z.array(lds1PurchaseReportRowSchema),
});
export type Lds1PurchaseReport = z.infer<typeof lds1PurchaseReportSchema>;

export const lds1PurchaseApi = {
  /* member-facing */
  info: () => get('/lds1-purchase/info', resource(lds1PurchaseInfoSchema)),
  list: (query: ListQuery = {}) => get('/lds1-purchase', paginated(lds1PurchaseRequestSchema), query),
  create: (input: { usdt_amount: number | string; recipient_wallet_address?: string }) =>
    post('/lds1-purchase', resource(lds1PurchaseResultSchema), input),
  recheck: (id: string) => post(`/lds1-purchase/${id}/recheck`, resource(lds1PurchaseResultSchema)),

  /* admin */
  admin: {
    settings: () => get('/admin/lds1-purchase/settings', resource(lds1PurchaseAdminSettingsSchema)),
    updateSettings: (input: {
      enabled: boolean;
      max_per_tx_usd: number | string;
      max_per_day_usd: number | string;
      gas_limit_units: number;
    }) => command('/admin/lds1-purchase/settings', input),
    updateDepositAddress: (address: string) =>
      post(
        '/admin/lds1-purchase/deposit-address',
        resource(z.object({ deposit_address: z.string() })),
        { address },
      ),
    /** Write-only: response is just the derived address, never the key. */
    setWalletKey: (privateKey: string) =>
      post(
        '/admin/lds1-purchase/wallet-key',
        resource(z.object({ wallet_address: z.string() })),
        { private_key: privateKey },
      ),
    clearWalletKey: () => del('/admin/lds1-purchase/wallet-key', z.object({ message: z.string() })),
    retry: (id: string) =>
      post(`/admin/lds1-purchase/${id}/retry`, resource(z.object({ send_tx_hash: z.string().nullable() }))),
    report: () => get('/admin/lds1-purchase/report', resource(lds1PurchaseReportSchema)),
  },
};
